import React from 'react';
import T from '../common/T';
import { X, Phone, Mail, MapPin, DollarSign, Briefcase, Tag } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// 🟢 SECURITY IMPORT
import { useAuth } from '../../context/AuthContext';

const LeadViewModal = ({ lead, isOpen, onClose }) => {
  const navigate = useNavigate();
  const { checkAccess } = useAuth(); // 🟢 GET CHECKER

  if (!isOpen || !lead) return null;

  const handleEditRedirect = () => {
    navigate('/crm/detail', { state: { lead } });
  };

  const getPriorityColor = (p) => {
    if (p === 'high') return 'text-red-600 bg-red-50 border-red-200';
    if (p === 'medium') return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-gray-600 bg-gray-50 border-gray-200';
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
        
        {/* Header */}
        <div className="flex justify-between items-start px-6 py-5 border-b border-gray-100 bg-gray-50/50">
          <div>
            <h2 className="text-base font-bold text-gray-900">{lead.title || "Lead Details"}</h2>
            <div className="flex items-center gap-2 mt-1">
              <span className={`px-2 py-0.5 text-xs font-bold uppercase rounded border ${getPriorityColor(lead.priority)}`}>
                {lead.priority || 'Normal'} Priority
              </span>
              <span className="text-sm text-gray-500">ID: #{lead.id?.slice(-6)}</span>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition p-1 hover:bg-gray-100 rounded-full">
            <X size={20} />
          </button>
        </div>

        {/* Body - Scrollable */}
        <div className="p-6 overflow-y-auto space-y-6">
          
          {/* Section 1: Key Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-50 text-blue-600 rounded-lg"><Briefcase size={18} /></div>
                <div>
                  <label className="text-xs font-bold text-gray-400 uppercase"><T>Client / Company</T></label>
                  <p className="font-semibold text-gray-800 text-lg">{lead.client || lead.company}</p>
                </div>
              </div>

              {/* 🟢 SECURED: Budget only visible if allowed */}
              {checkAccess('financial.view') && (
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-green-50 text-green-600 rounded-lg"><DollarSign size={18} /></div>
                    <div>
                      <label className="text-xs font-bold text-gray-400 uppercase"><T>Budget / Revenue</T></label>
                      <p className="font-semibold text-gray-800 text-lg">
                        {lead.budget ? `₹ ${lead.budget.toLocaleString()}` : 'Not Disclosed'}
                      </p>
                    </div>
                  </div>
              )}
            </div>

            <div className="space-y-4">
               <div className="flex items-start gap-3">
                <div className="p-2 bg-purple-50 text-purple-600 rounded-lg"><Tag size={18} /></div>
                <div>
                  <label className="text-xs font-bold text-gray-400 uppercase"><T>Requirement</T></label>
                  <p className="font-medium text-gray-800">{lead.configuration} {lead.unit ? `(${lead.unit})` : ''}</p>
                  <p className="text-sm text-gray-500 mt-0.5">{lead.stage} Stage</p>
                </div>
              </div>
            </div>
          </div>

          <hr className="border-gray-100" />

          {/* Section 2: Contact Info */}
          <div>
            <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
              Contact Information
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-center gap-3 p-3 border border-gray-100 rounded-lg bg-gray-50">
                <Phone size={16} className="text-gray-400" />
                <span className="text-sm font-medium text-gray-700">{lead.phone || "No Phone"}</span>
              </div>
              <div className="flex items-center gap-3 p-3 border border-gray-100 rounded-lg bg-gray-50">
                <Mail size={16} className="text-gray-400" />
                <span className="text-sm font-medium text-gray-700">{lead.email || "No Email"}</span>
              </div>
              <div className="flex items-center gap-3 p-3 border border-gray-100 rounded-lg bg-gray-50">
                <MapPin size={16} className="text-gray-400" />
                <span className="text-sm font-medium text-gray-700">{lead.location || "Location not set"}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
          <button 
            onClick={onClose}
            className="px-4 py-2 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition"
          >
            Close
          </button>
          
          {/* 🟢 SECURED: Edit button only if crm.edit */}
          {checkAccess('crm.edit') && (
              <button 
                onClick={handleEditRedirect}
                className="px-6 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 shadow-md transition flex items-center gap-2"
              >
                Full Edit Details
              </button>
          )}
        </div>

      </div>
    </div>
  );
};

export default LeadViewModal;